import { z } from 'zod';

/**
 * Canonical (typed) columns of the unified superset member record (PRD §5).
 * The long tail of carrier-specific fields lives in `extra`/`raw` JSONB.
 */
export const CANONICAL_COLUMNS = [
  // identity
  'member_external_id',
  'member_first_name',
  'member_last_name',
  'member_full_name',
  'member_dob',
  // contact
  'email',
  'phone',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  // policy / financial
  'policy_number',
  'premium_amount',
  'commission_amount',
  'commission_rate',
  'renewal_date',
  'effective_date',
  'plan_name',
  'plan_code',
  'currency',
  // commission context (recurs across carriers; see carrier configs)
  'writing_agent',
  'transaction_type',
] as const;

export type CanonicalColumn = (typeof CANONICAL_COLUMNS)[number];

export const CANONICAL_COLUMN_SET: ReadonlySet<string> = new Set(CANONICAL_COLUMNS);

export function isCanonicalColumn(name: string): name is CanonicalColumn {
  return CANONICAL_COLUMN_SET.has(name);
}

/** Numeric canonical columns (parsed/normalized from carrier strings). */
export const NUMERIC_COLUMNS: readonly CanonicalColumn[] = [
  'premium_amount',
  'commission_amount',
  'commission_rate',
] as const;

/** Date canonical columns. */
export const DATE_COLUMNS: readonly CanonicalColumn[] = [
  'member_dob',
  'renewal_date',
  'effective_date',
] as const;

/**
 * A fully mapped unified member record produced by the mapping engine.
 * `null` means "carrier did not supply this field".
 */
export interface UnifiedRecord {
  // deterministic identity
  record_ref_id: string;
  member_ref_id: string;
  policy_ref_id: string;
  /** Workspace partition this record belongs to (null for preview/unscoped). */
  workspace_id: string | null;
  // period
  period_year: number;
  period_month: number;
  // canonical member fields
  member_external_id: string;
  member_first_name: string | null;
  member_last_name: string | null;
  member_full_name: string | null;
  member_dob: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  policy_number: string | null;
  premium_amount: number | null;
  commission_amount: number;
  commission_rate: number | null;
  renewal_date: string | null;
  effective_date: string | null;
  plan_name: string | null;
  plan_code: string | null;
  currency: string;
  writing_agent: string | null;
  transaction_type: string | null;
  // provenance
  source_row_number: number;
  raw: Record<string, unknown>;
  extra: Record<string, unknown>;
}

/** Default set of tracked fields whose change triggers a yellow status (PRD §8). */
export const DEFAULT_TRACKED_FIELDS: readonly CanonicalColumn[] = [
  'commission_amount',
  'premium_amount',
  'renewal_date',
  'plan_name',
  'email',
  'phone',
] as const;

export const recordRefIdSchema = z.string().min(1);
export const memberRefIdSchema = z.string().min(1);
